import { FC, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import mavAeroIcon from '../assets/image/mav-aero_icon_32.png'
import mavLauncherIcon from '../assets/image/mav-launcher_icon_256.png'
import { useGenerateInstallerSasUrl, useGenerateVideoSasUrl } from '../api/upload-API'
import MavNavBar, { MAV_NAV_CSS } from '../components/MavNavBar'

const scrollTo = (id: string) => (e: React.MouseEvent) => {
  e.preventDefault()
  const el = document.getElementById(id)
  if (!el) return
  const navHeight = 64
  const top = el.getBoundingClientRect().top + window.scrollY - navHeight
  window.scrollTo({ top, behavior: 'smooth' })
}

// Video file names (stored in Azure Blob Storage)
const VIDEO_FILES = {
  sizzle: 'MAV-Aero_SizzleReel_FangioF1_2025-05-06.mp4',
  training: [
    'MAV-Aero_Tutorial_01_QuickStart.mp4',
    'MAV-Aero_Tutorial_02_VisualizerBasics.mp4',
    'MAV-Aero_Tutorial_03_VisualizerAdvanced.mp4',
    'MAV-Aero_Tutorial_04_ManagingDataSources.mp4',
    'MAV-Aero_Tutorial_05_VolumetricBasics.mp4',
    'MAV-Aero_Tutorial_06_SessionSharing.mp4',
    'MAV-Aero_Tutorial_07_SurfaceParts.mp4',
    'MAV-Aero_Tutorial_08_Streamlines.mp4',
    'MAV-Aero_Tutorial_09_LIC.mp4',
    'MAV-Aero_Tutorial_10_PointSelection.mp4',
    'MAV-Aero_Tutorial_11_SliceVectorGlyphs.mp4',
    'MAV-Aero_Tutorial_12_FreeOrbit.mp4',
    'MAV-Aero_Tutorial_13_ContentGrouping.mp4',
    'MAV-Aero_Tutorial_14_LayoutTemplates.mp4',
    'MAV-Aero_Tutorial_15_VolumeClipping.mp4',
    'MAV-Aero_Tutorial_16_AnimationSystem.mp4'
  ]
}

const USER_REFERENCE_URL = 'https://ms-aero-srvc.gm-motorsports.com/#/mav-aero-docs'
const RELEASE_NOTES_URL = 'https://ms-aero-srvc.gm-motorsports.com/#/mav-aero-docs'
const SLACK_SUPPORT_URL = 'https://gm.enterprise.slack.com/archives/C09SANE099D'
const FOOTER_SUPPORT_URL = 'https://gm.enterprise.slack.com/archives/C09SANE099D'

const getTitleFromUrl = (filename: string): string => {
  const cleanFilename = filename.replace(/\.mp4.*$/, '')
  const parts = cleanFilename.split('_')
  // parts: ['MAV-Aero', 'Tutorial', '01', 'QuickStart', ...]
  const num = parts[2] ?? ''
  const namePart = parts.slice(3).join(' ')
  const name = namePart.replace(/([A-Z])/g, ' $1').trim()
  return `${num} · ${name}`
}

const MAVAero: FC = () => {
  const [{ data: sasData, loading: sasLoading, error: sasError }, getSasUrl] =
    useGenerateInstallerSasUrl()
  const [{ data: videoSasData }, getVideoSasUrl] = useGenerateVideoSasUrl()

  const [isDownloading, setIsDownloading] = useState(false)
  const [videoUrls, setVideoUrls] = useState<{ [key: string]: string }>({})
  const [videosLoading, setVideosLoading] = useState(true)

  // Generate SAS URLs for all videos in parallel on mount.
  // Cancel them when leaving the page so a route change does not report each one as a network error.
  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false
    const generateVideoSasUrls = async () => {
      try {
        setVideosLoading(true)
        const urls: { [key: string]: string } = {}

        const allVideoFiles = [VIDEO_FILES.sizzle, ...VIDEO_FILES.training]
        const results = await Promise.allSettled(
          allVideoFiles.map(videoFile =>
            getVideoSasUrl({
              signal: controller.signal,
              data: {
                uploadType: 'MAV_VIDEO_FILE',
                blobName: videoFile,
                raceTeamId: ''
              }
            }).then(result => ({ videoFile, sasUrl: result?.data?.sasUrl }))
          )
        )

        if (cancelled) return
        for (const result of results) {
          if (result.status === 'fulfilled' && result.value.sasUrl) {
            urls[result.value.videoFile] = result.value.sasUrl
          }
        }

        setVideoUrls(urls)
      } catch (error) {
        if (!cancelled) console.error('Error generating video SAS URLs:', error)
      } finally {
        if (!cancelled) setVideosLoading(false)
      }
    }

    generateVideoSasUrls()
    return () => {
      cancelled = true
      controller.abort()
    }
  }, []) // Empty dependency - run once on mount

  // Helper to get SAS URL for video
  const getVideoUrl = (filename: string) => videoUrls[filename] || ''

  const handleDownload = async () => {
    try {
      setIsDownloading(true)

      // Request SAS URL from backend
      const result = await getSasUrl({
        data: {
          uploadType: 'MAV_INSTALLER_FILE',
          blobName: 'MAV-Installer.exe',
          raceTeamId: '' // Not needed for installer downloads
        }
      })

      if (result?.data?.sasUrl) {
        // Trigger download using the SAS URL
        const link = document.createElement('a')
        link.href = result.data.sasUrl
        link.download = 'MAV-Installer.exe'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        console.error('Failed to get SAS URL for installer download')
        alert('Download failed. Please contact support.')
      }
    } catch (error) {
      console.error('Error downloading installer:', error)
      alert('Download failed. Please contact support.')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <>
      <style>{`
        :root {
          --mav-blue: rgb(0, 114, 206);
          --mav-blue-dark: rgb(0, 90, 165);
          --mav-blue-light: rgb(229, 242, 255);
          --mav-text: #1a1a2e;
          --mav-muted: #5a6475;
          --mav-border: #e2e8f0;
        }

        ${MAV_NAV_CSS}

        /* ── HERO ── */
        .mav-hero {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 24px 24px 0px;
          background: linear-gradient(160deg, #fff 60%, var(--mav-blue-light) 100%);
        }
        .mav-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: var(--mav-blue-light);
          color: var(--mav-blue);
          font-size: .8rem;
          font-weight: 600;
          letter-spacing: .06em;
          text-transform: uppercase;
          padding: 4px 14px;
          border-radius: 20px;
          margin-bottom: 24px;
        }
        .mav-hero h1 {
          font-size: clamp(2.4rem, 5vw, 3.8rem);
          font-weight: 800;
          line-height: 1.15;
          max-width: 700px;
          margin-bottom: 20px;
          letter-spacing: -.02em;
          color: var(--mav-text);
        }
        .mav-hero h1 span { color: var(--mav-blue); }
        .mav-hero p {
          font-size: 1.15rem;
          color: var(--mav-muted);
          max-width: 540px;
          margin-bottom: 44px;
        }
        .mav-btn-download {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: var(--mav-blue);
          color: #fff;
          font-size: 1.05rem;
          font-weight: 600;
          padding: 16px 36px;
          border-radius: 10px;
          text-decoration: none;
          box-shadow: 0 4px 20px rgba(0, 114, 206, .35);
          transition: background .2s, transform .15s, box-shadow .2s;
        }
        .mav-btn-download:hover {
          background: var(--mav-blue-dark);
          transform: translateY(-2px);
          box-shadow: 0 8px 28px rgba(0, 114, 206, .4);
          color: #fff;
        }
        .mav-btn-download svg { flex-shrink: 0; }
        .mav-hero-meta {
          margin-top: 18px;
          font-size: .82rem;
          color: #9aa3b0;
        }
        .mav-hero-visual {
          margin-top: 0px;
          margin-bottom: 20px;
          width: 100%;
          max-width: 860px;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0,0,0,.12);
          border: 1px solid var(--mav-border);
        }
        .mav-hero-video {
          display: block;
          width: 100%;
          aspect-ratio: 16 / 9;
          background: #0a1628;
        }

        /* ── SECTIONS ── */
        .mav-section { padding: 96px 24px; }
        .mav-section-inner { max-width: 1080px; margin: 0 auto; }
        .mav-section-tag {
          display: inline-block;
          color: var(--mav-blue);
          font-size: .78rem;
          font-weight: 700;
          letter-spacing: .1em;
          text-transform: uppercase;
          margin-bottom: 12px;
        }
        .mav-section-title {
          font-size: clamp(1.6rem, 3vw, 2.2rem);
          font-weight: 800;
          margin-bottom: 12px;
          letter-spacing: -.015em;
          color: var(--mav-text);
        }
        .mav-section-subtitle {
          font-size: 1rem;
          color: var(--mav-muted);
          max-width: 100%;
          margin-bottom: 56px;
        }
        .mav-divider {
          width: 48px;
          height: 4px;
          background: var(--mav-blue);
          border-radius: 2px;
          margin: 16px 0 20px;
        }

        /* ── DOCS ── */
        .mav-resources { background: #fff; }
        .mav-resources-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }
        @media (max-width: 1024px) {
          .mav-resources-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 600px) {
          .mav-resources-grid { grid-template-columns: 1fr; }
        }
        .mav-resource-card {
          border: 1px solid var(--mav-border);
          border-radius: 12px;
          padding: 28px;
          text-decoration: none;
          color: inherit;
          transition: border-color .2s, box-shadow .2s, transform .15s;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .mav-resource-card:hover {
          border-color: var(--mav-blue);
          box-shadow: 0 6px 24px rgba(0, 114, 206, .12);
          transform: translateY(-3px);
        }
        .mav-resource-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: var(--mav-blue-light);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .mav-resource-card h3 { font-size: 1rem; font-weight: 700; color: var(--mav-text); }
        .mav-resource-card p { font-size: .875rem; color: var(--mav-muted); }
        .mav-resource-arrow {
          margin-top: auto;
          color: var(--mav-blue);
          font-size: .85rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        /* ── VIDEOS ── */
        .mav-videos { background: var(--mav-blue-light); }
        .mav-videos-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 28px;
        }
        .mav-video-card {
          background: #fff;
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 2px 12px rgba(0,0,0,.07);
          transition: box-shadow .2s, transform .15s;
          cursor: pointer;
        }
        .mav-video-card:hover {
          box-shadow: 0 8px 28px rgba(0, 114, 206, .15);
          transform: translateY(-3px);
        }
        .mav-videos-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
          margin-bottom: 56px;
        }
        .mav-videos-header-left { flex: 1; }
        .mav-videos-header-left .mav-section-subtitle { margin-bottom: 0; }
        .mav-btn-view-all {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          border: 1.5px solid var(--mav-blue);
          color: var(--mav-blue);
          background: #fff;
          font-size: .9rem;
          font-weight: 600;
          padding: 10px 22px;
          border-radius: 8px;
          text-decoration: none;
          white-space: nowrap;
          transition: background .2s, color .2s;
          flex-shrink: 0;
          align-self: center;
        }
        .mav-btn-view-all:hover {
          background: var(--mav-blue);
          color: #fff;
        }
        .mav-training-video {
          display: block;
          width: 100%;
          aspect-ratio: 16 / 9;
          background: #0a1628;
        }
        .mav-video-info { padding: 14px 16px 18px; }
        .mav-video-info h3 { font-size: .9rem; font-weight: 700; margin-bottom: 0; color: var(--mav-text); }

        /* ── LAUNCHER ── */
        .mav-launcher { background: #fff; }
        .mav-launcher-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 64px;
          align-items: center;
        }
        @media (max-width: 768px) {
          .mav-launcher-layout { grid-template-columns: 1fr; gap: 32px; }
          .mav-nav { padding: 0 20px; }
        }
        .mav-launcher-steps { display: flex; flex-direction: column; gap: 24px; }
        .mav-step { display: flex; gap: 18px; align-items: flex-start; }
        .mav-step-num {
          flex-shrink: 0;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: var(--mav-blue);
          color: #fff;
          font-weight: 700;
          font-size: .9rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .mav-step-body h3 { font-size: .95rem; font-weight: 700; margin-bottom: 4px; color: var(--mav-text); }
        .mav-step-body p { font-size: .875rem; color: var(--mav-muted); }
        .mav-launcher-visual {
          background: linear-gradient(145deg, #0a1628 0%, #0d2f5e 100%);
          border-radius: 16px;
          padding: 32px;
          box-shadow: 0 16px 48px rgba(0, 0, 0, .18);
        }
        .mav-launcher-window-bar { display: flex; gap: 7px; margin-bottom: 24px; }
        .mav-launcher-window-bar span { width: 12px; height: 12px; border-radius: 50%; }
        .mav-launcher-window-bar span:nth-child(1) { background: #ff5f57; }
        .mav-launcher-window-bar span:nth-child(2) { background: #febc2e; }
        .mav-launcher-window-bar span:nth-child(3) { background: #28c840; }
        .mav-launcher-lines { display: flex; flex-direction: column; gap: 10px; }
        .mav-l-line { height: 10px; border-radius: 5px; background: rgba(255,255,255,.08); }
        .mav-l-line.accent { background: rgba(0, 114, 206, .5); width: 55%; }
        .mav-l-line.short { width: 40%; }
        .mav-l-line.medium { width: 70%; }
        .mav-l-line.long { width: 90%; }
        .mav-launcher-btn-row { margin-top: 28px; display: flex; gap: 12px; }
        .mav-lbtn { padding: 10px 22px; border-radius: 7px; font-size: .85rem; font-weight: 600; border: none; cursor: pointer; }
        .mav-lbtn-primary { background: var(--mav-blue); color: #fff; }
        .mav-lbtn-ghost { background: rgba(255,255,255,.1); color: rgba(255,255,255,.7); }
        .mav-tip {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-top: 48px;
          padding: 16px 20px;
          background: var(--mav-blue-light);
          border-left: 4px solid var(--mav-blue);
          border-radius: 8px;
        }
        .mav-tip-icon {
          flex-shrink: 0;
          color: var(--mav-blue);
          margin-top: 1px;
        }
        .mav-tip-text { font-size: .875rem; color: var(--mav-text); line-height: 1.5; }
        .mav-tip-text strong { font-weight: 700; }

        /* ── MAV-LAUNCHER APP SECTION ── */
        .mav-launcher-app { background: var(--mav-blue-light); }
        .mav-launcher-app-layout {
          display: grid;
          grid-template-columns: auto 1fr;
          gap: 56px;
          align-items: center;
        }
        @media (max-width: 640px) {
          .mav-launcher-app-layout { grid-template-columns: 1fr; gap: 32px; text-align: center; }
          .mav-launcher-app-icon-wrap { justify-content: center; }
        }
        .mav-launcher-app-icon-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .mav-launcher-app-icon {
          width: 128px;
          height: 128px;
          border-radius: 6px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, .15);
        }

        /* ── FULL-BLEED WRAPPER ── */
        .mav-page-wrapper {
          width: calc(100% + 4rem);
          margin-left: -2rem;
          margin-right: -2rem;
          align-self: stretch;
        }

        /* ── DARK MODE ── */
        body.dark {
          --mav-blue-light: rgba(0, 114, 206, 0.15);
          --mav-text: #e2e8f0;
          --mav-muted: #94a3b8;
          --mav-border: #2d3748;
        }
        body.dark .mav-nav {
          background: #06080c;
        }
        body.dark .mav-hero {
          background: linear-gradient(160deg, #0c1018 60%, #0a2042 100%);
        }
        body.dark .mav-hero-visual {
          border-color: var(--mav-border);
        }
        body.dark .mav-resources {
          background: #0c1018;
        }
        body.dark .mav-resource-card {
          background: #1e293b;
          color: var(--mav-text);
        }
        body.dark .mav-videos {
          background: #0a1628;
        }
        body.dark .mav-video-card {
          background: #1e293b;
        }
        body.dark .mav-launcher {
          background: #0c1018;
        }
        body.dark .mav-launcher-app {
          background: rgba(0, 114, 206, .08);
        }
        body.dark .mav-step-body h3,
        body.dark .mav-section-title,
        body.dark .mav-resource-card h3,
        body.dark .mav-video-info h3 {
          color: var(--mav-text);
        }
        body.dark .mav-tip {
          background: rgba(0, 114, 206, .12);
          border-left-color: var(--mav-blue);
        }
        body.dark .mav-btn-view-all {
          background: transparent;
        }
        body.dark .mav-footer {
          background: #080d1a;
          border-top-color: var(--mav-border);
        }
        body.dark .mav-footer p,
        body.dark .mav-hero-meta {
          color: #64748b;
        }

        /* ── FOOTER ── */
        .mav-footer {
          background: #f8fafc;
          border-top: 1px solid var(--mav-border);
          padding: 40px 48px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
        }
        .mav-footer-brand { font-weight: 700; color: var(--mav-blue); font-size: 1rem; }
        .mav-footer p { font-size: .82rem; color: #9aa3b0; }
        .mav-footer-links { display: flex; gap: 24px; }
        .mav-footer-links a { font-size: .82rem; color: var(--mav-muted); text-decoration: none; }
        .mav-footer-links a:hover { color: var(--mav-blue); }
      `}</style>

      <div className="mav-page-wrapper">
        {/* NAV */}
        <MavNavBar scrollTo={scrollTo} />

        {/* HERO */}
        <section className="mav-hero">
          <div className="mav-hero-badge">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="rgb(0,114,206)">
              <circle cx="5" cy="5" r="5" />
            </svg>
            Now Available
          </div>

          <div className="mav-hero-visual">
            {videosLoading ? (
              <div style={{ padding: '100px', textAlign: 'center', color: 'var(--mav-muted)' }}>
                Loading video...
              </div>
            ) : (
              <video
                className="mav-hero-video"
                src={getVideoUrl(VIDEO_FILES.sizzle)}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
              />
            )}
          </div>

          <h1>
            Aerodynamic visualization,
            <br />
            <span>built for performance.</span>
          </h1>
          <p>
            MAV-Aero streamlines your CFD workflow — fast, reliable, and built for motorsports
            engineering.
          </p>

          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className="mav-btn-download"
            style={{
              cursor: isDownloading ? 'wait' : 'pointer',
              opacity: isDownloading ? 0.7 : 1
            }}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {isDownloading ? 'Downloading...' : 'Download MAV-Installer'}
          </button>
          <p className="mav-hero-meta">Windows 11 &nbsp;· &nbsp; 39 MB</p>
          <p className="mav-hero-meta" style={{ marginTop: '6px' }}>
            Requires NVIDIA RTX 5000 Ada or Better
            <br />
            16 GB VRAM &nbsp;· &nbsp; 32 GB RAM
          </p>
        </section>

        {/* SETUP */}
        <section id="mav-setup" className="mav-section mav-launcher">
          <div className="mav-section-inner">
            <div className="mav-launcher-layout">
              <div>
                <span className="mav-section-tag">Setup</span>
                <h2 className="mav-section-title">Up and running in minutes</h2>
                <div className="mav-divider" />
                <p className="mav-section-subtitle" style={{ marginBottom: '40px' }}>
                  The MAV-Aero Launcher manages updates, environments, and authentication — so
                  you're always on the right version.
                </p>

                <div className="mav-launcher-steps">
                  {[
                    {
                      n: 1,
                      title: 'Download & Install',
                      desc: 'Run the installer and a MAV-Launcher shortcut will be placed on your desktop and in your start menu automatically.'
                    },
                    {
                      n: 2,
                      title: 'Sign In',
                      desc: "Authenticate with your organisation's Azure AD account — single sign-on supported."
                    },
                    {
                      n: 3,
                      title: 'Stay Updated',
                      desc: 'MAV-Aero auto-detects new releases and prompts you to update with one click.'
                    }
                  ].map(s => (
                    <div className="mav-step" key={s.n}>
                      <div className="mav-step-num">{s.n}</div>
                      <div className="mav-step-body">
                        <h3>{s.title}</h3>
                        <p>{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mav-launcher-visual">
                <div className="mav-launcher-window-bar">
                  <span />
                  <span />
                  <span />
                </div>
                <div className="mav-launcher-lines">
                  <div className="mav-l-line accent" />
                  <div style={{ height: '24px' }} />
                  <div className="mav-l-line long" />
                  <div className="mav-l-line medium" />
                  <div className="mav-l-line short" />
                  <div style={{ height: '12px' }} />
                  <div className="mav-l-line long" />
                  <div className="mav-l-line medium" />
                  <div style={{ height: '12px' }} />
                  <div className="mav-l-line short" />
                  <div className="mav-l-line long" />
                  <div className="mav-l-line medium" />
                </div>
                <div className="mav-launcher-btn-row">
                  <button className="mav-lbtn mav-lbtn-primary">Launch MAV-Aero</button>
                  <button className="mav-lbtn mav-lbtn-ghost">Settings</button>
                </div>
              </div>
            </div>

            <div className="mav-tip">
              <svg
                className="mav-tip-icon"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <p className="mav-tip-text">
                <strong>Self-updating:</strong> Once installed, the MAV-Launcher checks for new
                versions automatically each time it starts. When an update is available you'll be
                prompted to install it with a single click — no manual downloads required.
              </p>
            </div>
            <div className="mav-tip">
              <svg
                className="mav-tip-icon"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <p className="mav-tip-text">
                <strong>Clean up after install:</strong> Once the installation is complete, the{' '}
                <strong>MAV-Installer.exe</strong> file can be safely deleted — it is no longer
                needed.
              </p>
            </div>
          </div>
        </section>
        <section id="mav-launcher-app" className="mav-section mav-launcher-app">
          <div className="mav-section-inner">
            <div className="mav-launcher-app-layout">
              <div className="mav-launcher-app-icon-wrap">
                <img src={mavLauncherIcon} alt="MAV-Launcher" className="mav-launcher-app-icon" />
              </div>
              <div>
                <span className="mav-section-tag">MAV-Launcher</span>
                <h2 className="mav-section-title">Your gateway to MAV-Aero</h2>
                <div className="mav-divider" />
                <p className="mav-section-subtitle" style={{ marginBottom: '20px' }}>
                  The installer also includes <strong>MAV-Launcher</strong>. Once installation is
                  complete, a shortcut icon is automatically created on your{' '}
                  <strong>Desktop</strong> and in the <strong>Start Menu</strong> — no extra steps
                  required.
                </p>
                <p className="mav-section-subtitle" style={{ marginBottom: 0 }}>
                  Use MAV-Launcher to open the last version of MAV-Aero you had in use. It handles
                  version management, authentication, and updates so you always pick up exactly
                  where you left off.
                </p>
              </div>
            </div>
          </div>
        </section>
        <section id="mav-videos" className="mav-section mav-videos">
          <div className="mav-section-inner">
            <div className="mav-videos-header">
              <div className="mav-videos-header-left">
                <span className="mav-section-tag">Video Tutorials</span>
                <h2 className="mav-section-title">Learn by watching</h2>
                <div className="mav-divider" />
                <p className="mav-section-subtitle">
                  Step-by-step video walkthroughs to get you going quickly.
                </p>
              </div>
            </div>

            <div className="mav-videos-grid">
              {videosLoading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--mav-muted)' }}>
                  Loading training videos...
                </div>
              ) : (
                VIDEO_FILES.training.map(videoFile => (
                  <div className="mav-video-card" key={videoFile}>
                    <video
                      className="mav-training-video"
                      src={getVideoUrl(videoFile)}
                      controls
                      playsInline
                      preload="metadata"
                    />
                    <div className="mav-video-info">
                      <h3>{getTitleFromUrl(videoFile)}</h3>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>

        {/* DOCS */}
        <section id="mav-resources" className="mav-section mav-resources">
          <div className="mav-section-inner">
            <span className="mav-section-tag">Resources</span>
            <h2 className="mav-section-title">Everything you need to get started</h2>
            <div className="mav-divider" />
            <p className="mav-section-subtitle">
              Guides covering installation, configuration, and advanced usage patterns.
            </p>

            <div className="mav-resources-grid">
              <a target="_blank" href={USER_REFERENCE_URL} className="mav-resource-card">
                <div className="mav-resource-icon">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="rgb(0,114,206)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                  </svg>
                </div>
                <h3>User Guide</h3>
                <p>Reference documentation for working with MAV-Aero.</p>
                <span className="mav-resource-arrow">Open link →</span>
              </a>

              <a target="_blank" href={RELEASE_NOTES_URL} className="mav-resource-card">
                <div className="mav-resource-icon">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="rgb(0,114,206)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                  </svg>
                </div>
                <h3>Release Notes</h3>
                <p>Changelogs, bug fixes, and new feature summaries for every version.</p>
                <span className="mav-resource-arrow">Open link →</span>
              </a>

              <a target="_blank" href={SLACK_SUPPORT_URL} className="mav-resource-card">
                <div className="mav-resource-icon">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="rgb(0,114,206)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <h3>GM Support (Slack)</h3>
                <p>Get in touch with our support team for assistance.</p>
                <span className="mav-resource-arrow">Open Slack →</span>
              </a>
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="mav-footer">
          <div>
            <div className="mav-footer-brand">MAV-Aero</div>
            <p style={{ marginTop: '4px' }}>© 2026 GM Motorsports. All rights reserved.</p>
          </div>
          <div className="mav-footer-links">
            <a href="#mav-setup" onClick={scrollTo('mav-setup')}>
              Setup
            </a>
            <a href="#mav-videos" onClick={scrollTo('mav-videos')}>
              Tutorials
            </a>
            <Link to="/mav-aero-docs" style={{ textDecoration: 'none' }}>
              Docs
            </Link>
            <a href={FOOTER_SUPPORT_URL}>Support</a>
          </div>
        </footer>
      </div>
    </>
  )
}

export default MAVAero
